import { useCallback, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore, type MutableRefObject } from "react";
import { Icon } from "./Icon";
import { MediaStage } from "./MediaStage";
import { RoundVideoPlayer } from "./RoundVideo";
import { VoicePlayer } from "./VoiceNote";
import { useTopLayerMenu } from "./useTopLayerMenu";
import type { ReviewRecorder } from "./ReviewMedia";
import {
  composerAction, composerMenu, containerLabel, containerState, MIC_HOLD_MS, photoIconClick, VIDEO_UNSUPPORTED,
  type ComposerMode, type MediaKind, type SlotKey,
} from "../lib/reviewMedia";
import { subscribeUploads, uploadStatus, uploadsVersion } from "../lib/mediaUpload";
import { PHOTO_ACCEPT } from "../lib/reviewPhoto";
import { voiceClock } from "../lib/messages";

/* 🔴 THE REVIEW'S COMPOSER ON THE WEB (job 3 step 5 page 2, 2026-10-09) — his 10-07 idea (CLAUDE.md DECIDED: "The review's
 * comment is a COMPOSER … + (system menu Video · Camera · Photos) · a field `Comment` that grows · the mic inside, tap or
 * hold"), the phone's `ReviewComposer.swift` by the SAME names (`vocabulary-mismatch`):
 *
 *   `[+]  [ takes … / Comment ……  🎙 ]`
 *
 * - the + opens ONE menu: `Video` · `Live review` (the overall only) · `Photos` — `composerMenu`, the phone's words in its
 *   order; the phone's `Camera` is not drawn (the web's camera records video only — MEASURED, `RoundVideo.tsx`);
 * - the field is the CONTENT layer — the card's raised ground, a capsule (radius 18), never glass (HIG Materials); its
 *   words grow by JS one line → `maxHeight` then scroll (William's Ventura Safari has no `field-sizing`);
 * - its takes sit INSIDE, above the words: the voice note's player, the round video (▶ + clock, opens round), the photo —
 *   ONE of each, each with its ×; while one is held its menu item waits (the × first), so nothing silently replaces;
 * - the mic: a CLICK starts and ■ keeps, or a HOLD (`MIC_HOLD_MS`, the phone's 0.3 s) records while held and keeps it on
 *   release — one button in both states (mic ↔ ■), so a hold survives the swap. While THIS slot records (voice, video or
 *   the live review) the field BECOMES the recorder in place: red dot · clock · live bars · ■, the + waits.
 * The page's ONE recorder (`useReviewRecorder`) stays the engine: starting another recording stops this one. Nothing is
 * uploaded here — the takes are Blobs in this tab until the review's Send.
 *
 * 🔴 THE ONE COMPOSER (his 10-09 word: "use it in general for the app") — the Inbox thread's reply is THIS component (job 3
 * step 5 page 5), by props, never a copy: `plus={false}` (a message carries words or a voice note, nothing the + offers),
 * `mic` behind the voice probe, and `send` — the W84-4 switch, the phone's `showsMic`: an empty field shows the mic, words
 * or a held voice note show Send (the one primary, a round ↑ where the mic stood) inside the field; with no mic Send stays,
 * dimmed while there is nothing to send. Send is the caller's `<form>`'s submit; Enter is the caller's `onKeyDown`. */
export function ReviewComposer({ slot, r, locked, sendingPath, name, id, label, placeholder, text, onText, maxHeight, live = false, autoFocus = false,
                                 plus = true, mic = true, send, onKeyDown, inputRef, hint, maxLength }: {
  slot: SlotKey; r: ReviewRecorder; locked: boolean; sendingPath: (kind: MediaKind) => string | null;
  /** What the composer is about, for every control's name (`HS — Balance Attempt`, `the whole workout`). */
  name: string;
  /** The field's id and its (visually hidden) label. */
  id: string; label: string;
  placeholder: "Comment" | "Overall" | "Reply" | "Message";
  text: string; onText: (text: string) => void;
  /** The field's tallest, in px, before it scrolls (a comment ~6 lines, the overall ~8). */
  maxHeight: number;
  /** The overall's `Live review` (the tab, recorded with the face); a comment and a reply have none. */
  live?: boolean;
  /** A reply opened from its `Reply` doorway takes the keyboard at once. */
  autoFocus?: boolean;
  /** The + and its menu (Video · Live review · Photos); the thread has none. */
  plus?: boolean;
  /** The mic inside the field (the thread: only once its voice probe found the bucket). */
  mic?: boolean;
  /** The thread's Send — present = the mic/Send switch; `busy` while the message goes. */
  send?: { busy: boolean };
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  /** The caller's handle on the field (the thread's Reply puts the keyboard in it). */
  inputRef?: MutableRefObject<HTMLTextAreaElement | null>;
  /** Said to assistive technology about the field (`aria-describedby`), and the Send button's tooltip. */
  hint?: string;
  maxLength?: number;
}) {
  useSyncExternalStore(subscribeUploads, uploadsVersion, uploadsVersion);
  const [menu, setMenu] = useState(false);
  const [stage, setStage] = useState<"video" | "photo" | null>(null);
  const plusButton = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const pick = useRef<HTMLInputElement>(null);
  const hold = useRef<{ timer: number | null; held: boolean }>({ timer: null, held: false });
  useTopLayerMenu(menu, plusButton, list);
  useEffect(() => {
    if (menu) list.current?.querySelector<HTMLButtonElement>("button:not(:disabled)")?.focus();
  }, [menu]);
  useEffect(() => () => { if (hold.current.timer !== null) window.clearTimeout(hold.current.timer); }, []);
  const field = useRef<HTMLTextAreaElement | null>(null);
  const grow = useCallback((el: HTMLTextAreaElement | null) => {
    field.current = el;
    if (inputRef !== undefined) inputRef.current = el;
    autoGrow(el, maxHeight);
  }, [maxHeight, inputRef]);
  // A field emptied from outside (the thread's Send clears it) shrinks back to its one line.
  useLayoutEffect(() => { autoGrow(field.current, maxHeight); }, [text, maxHeight]);

  const takes = r.rec.slots[slot];
  const active = r.rec.active;
  const here = active !== null && active.slot === slot ? active : null;
  const items = composerMenu(takes, { live, videoEngine: r.video.mime !== null });
  const stateOf = (kind: MediaKind) => {
    const path = takes?.[kind] === undefined ? null : sendingPath(kind);
    return containerState({ filled: takes?.[kind] !== undefined, recording: false, upload: path === null ? null : uploadStatus(path) });
  };

  function close(restoreFocus: boolean) {
    setMenu(false);
    if (restoreFocus) plusButton.current?.focus();
  }
  function choose(mode: ComposerMode) {
    close(false);
    if (mode === "photos") pick.current?.click();
    else r.toggle(slot, mode);
  }
  function onMenuKey(e: React.KeyboardEvent) {
    if (e.key === "Escape") { e.stopPropagation(); close(true); return; }
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    const buttons = [...(list.current?.querySelectorAll<HTMLButtonElement>("button:not(:disabled)") ?? [])];
    const at = buttons.findIndex((b) => b === document.activeElement);
    buttons[(at + (e.key === "ArrowDown" ? 1 : buttons.length - 1)) % buttons.length]?.focus();
  }

  // ── the mic: click = start / stop; hold = record while held, release keeps it ──
  const voiceHeld = takes?.voice !== undefined;
  const micOff = locked || (here === null && (voiceHeld || r.voice.mime === null));
  const stopHere = () => { if (here !== null) r.toggle(slot, here.kind === "voice" ? "voice" : here.live ? "live" : "video"); };
  function endHold() {
    if (hold.current.timer !== null) { window.clearTimeout(hold.current.timer); hold.current.timer = null; return; }   // a tap: the click does it
    if (hold.current.held && r.rec.active?.slot === slot && r.rec.active.kind === "voice") r.toggle(slot, "voice");
  }

  const clock = here === null ? "" : voiceClock(here.kind === "voice" ? r.voice.elapsed : r.video.elapsed);
  const still = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
  const bars = here?.kind === "voice" ? (still ? Array<number>(18).fill(0.4) : r.voice.levels.slice(-18)) : [];
  const voice = takes?.voice, video = takes?.video, photo = takes?.photo;
  // W84-4 — the mic/Send switch (a thread and the review's reply): nothing to send → the mic (while there is one); else Send.
  // ANY take held is something to send (a photo alone is a reply — job 3 step 6 page 2).
  const held = voice !== undefined || video !== undefined || photo !== undefined;
  const nothing = text.trim() === "" && !held;
  const action = composerAction({ send: send !== undefined, mic, canRecord: r.voice.mime !== null, recording: here !== null, text, held });
  const photoState = stateOf("photo");
  const photoUrl = photo === undefined ? undefined : r.urlOf(photo.ref);
  const showTakes = here === null && (voice !== undefined || video !== undefined || photo !== undefined);
  const x = (kind: MediaKind, words: string) => (stateOf(kind) === "filled" && !locked
    ? <button type="button" className="review-composer-x" aria-label={`${words} on ${name}`}
              onClick={() => { setStage(null); r.remove(slot, kind); }}><Icon name="x" size={10} /></button>
    : null);
  const status = (kind: MediaKind, seconds: number) => {
    const s = stateOf(kind);
    return s === "filled" ? null : <span className="review-composer-take is-status" role="status">{containerLabel(kind === "voice" ? "voice" : "video", s, seconds)}</span>;
  };

  return (
    <div className={here === null ? "review-composer" : "review-composer is-recording"}>
      {plus && <button ref={plusButton} type="button" className="review-composer-plus" aria-haspopup="menu" aria-expanded={menu}
              aria-label={live ? `Add a video, a live review or a photo on ${name}` : `Add a video or a photo on ${name}`}
              disabled={locked || here !== null} onClick={() => setMenu(!menu)}>
        <Icon name="plus" size={18} />
      </button>}
      {plus && menu && (
        <ul ref={list} className="review-composer-menu is-top-layer" role="menu" aria-label={`Add to ${name}`} onKeyDown={onMenuKey}>
          {items.map((it) => (
            <li key={it.mode} role="none">
              <button type="button" role="menuitem" className="review-composer-item" disabled={it.disabled}
                      title={it.mode !== "photos" && r.video.mime === null ? VIDEO_UNSUPPORTED : undefined}
                      onClick={() => choose(it.mode)}>
                <Icon name={it.mode === "video" ? "video" : it.mode === "live" ? "monitor" : "image"} size={16} />{it.word}
              </button>
            </li>
          ))}
        </ul>
      )}
      {/* A click outside closes it WITHOUT taking the focus back (the coach has moved on) — `WeekMenu`'s rule. */}
      {menu && <div className="review-composer-scrim" onClick={() => close(false)} aria-hidden="true" />}

      <div className={here === null ? "review-composer-field" : "review-composer-field is-recording"}>
        {showTakes && (
          <div className="review-composer-takes">
            {voice !== undefined && (status("voice", voice.seconds) ?? (r.urlOf(voice.ref) !== undefined && (
              <div className="review-composer-take is-voice">
                <VoicePlayer compact load={{ status: "ready", url: r.urlOf(voice.ref) ?? "", bars: [] }} seconds={voice.seconds} unavailable={null} />
                {x("voice", "Remove the voice note")}
              </div>
            )))}
            {video !== undefined && (status("video", video.seconds) ?? (
              <div className="review-composer-take">
                <button type="button" className="review-composer-video" onClick={() => setStage("video")}
                        aria-label={video.live ? `Play your live review on ${name}` : `Play your video on ${name}, ${voiceClock(video.seconds)}`}>
                  <Icon name="play" size={12} /><span>{voiceClock(video.seconds)}</span>
                </button>
                {x("video", video.live ? "Remove the live review" : "Remove the video")}
              </div>
            ))}
            {photo !== undefined && (status("photo", 0) ?? (
              <div className="review-composer-take">
                <button type="button" className="review-composer-photo" aria-label={`Open your photo on ${name}`}
                        onClick={() => { if (photoIconClick(photoState) === "open") setStage("photo"); }}>
                  {photoUrl !== undefined && <img src={photoUrl} alt="" />}
                </button>
                {x("photo", "Remove the photo")}
              </div>
            ))}
          </div>
        )}
        <div className="review-composer-line">
          {here !== null ? (
            <span className="review-composer-recorder" role="status" aria-label={`Recording ${here.kind === "voice" ? "voice" : "video"}, ${clock}`}>
              <span className="review-composer-dot" aria-hidden="true" />
              <span>{here.kind === "voice" ? clock : `Video ${clock}`}</span>
              {bars.length > 0 && (
                <span className="review-composer-bars" aria-hidden="true">
                  {bars.map((l, i) => <span key={i} style={{ height: `${String(Math.round(4 + 14 * l))}px` }} />)}
                </span>
              )}
            </span>
          ) : (
            <>
              <label className="ht-visually-hidden" htmlFor={id}>{label}</label>
              <textarea id={id} className="review-composer-input" rows={1} ref={grow} placeholder={placeholder} value={text} autoFocus={autoFocus}
                        disabled={locked} maxLength={maxLength} onKeyDown={onKeyDown} aria-describedby={hint === undefined ? undefined : `${id}-hint`}
                        onChange={(e) => { onText(e.target.value); grow(e.target); }} />
              {hint !== undefined && <span id={`${id}-hint`} className="ht-visually-hidden">{hint}</span>}
            </>
          )}
          {/* ONE button in both states (mic ↔ ■), so a hold that began on the mic still ends on release after the swap. */}
          {action === "mic" ? <button type="button" className={here === null ? "review-composer-mic" : "review-composer-mic is-stop"} disabled={micOff}
                  aria-label={here !== null ? `Stop recording on ${name}` : `Record voice on ${name}`}
                  title={here !== null ? undefined : r.voice.mime === null ? "This browser cannot record voice notes the phone can play."
                    : "Click to start; or hold to record while held"}
                  onPointerDown={(e) => {
                    if (e.button !== 0 || micOff || here !== null) return;
                    hold.current.held = false;
                    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* a pointer the browser no longer knows: no capture */ }
                    hold.current.timer = window.setTimeout(() => { hold.current.timer = null; hold.current.held = true; r.toggle(slot, "voice"); }, MIC_HOLD_MS);
                  }}
                  onPointerUp={endHold} onPointerCancel={endHold}
                  onClick={() => {
                    if (hold.current.held) { hold.current.held = false; return; }   // the hold already started and kept it
                    if (here !== null) stopHere(); else r.toggle(slot, "voice");
                  }}>
            {here !== null ? <span className="review-composer-stop" aria-hidden="true" /> : <Icon name="mic" size={17} />}
          </button> : action === "send" && send !== undefined && (
            <button type="submit" className="ht-button ht-button-primary review-composer-send" aria-label={send.busy ? "Sending…" : "Send"}
                    title={hint} disabled={send.busy || nothing}>
              <Icon name="arrow-up" size={16} />
            </button>
          )}
        </div>
      </div>

      {plus && <input ref={pick} type="file" accept={PHOTO_ACCEPT} hidden tabIndex={-1}
             onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f !== undefined) void r.addPhoto(slot, f); }} />}
      {stage === "photo" && photoUrl !== undefined && (
        <MediaStage shape="photo" label={`Your photo on ${name}`} onClose={() => setStage(null)}>
          <img className="media-stage-media" src={photoUrl} alt="" />
        </MediaStage>
      )}
      {stage === "video" && video !== undefined && r.urlOf(video.ref) !== undefined && (
        <MediaStage shape="round" label={`Your video on ${name}`} onClose={() => setStage(null)}>
          <RoundVideoPlayer src={r.urlOf(video.ref) ?? null} seconds={video.seconds} unavailable={null} />
        </MediaStage>
      )}
    </div>
  );
}

/** The composer's tallest: a comment or a reply ~6 lines (20 px each + its padding), the overall ~8. */
export const COMPOSER_MAX = 132;
export const COMPOSER_OVERALL_MAX = 178;

/** The field grows with its lines (one line to `max` px, then it scrolls) — by JS, since Safari 16/17 (William's Ventura
 *  Mac) has no `field-sizing: content`. A ref callback too, so a restored draft opens at its own height. */
export function autoGrow(el: HTMLTextAreaElement | null, max: number): void {
  if (el === null) return;
  el.style.height = "auto";
  const h = Math.min(max, el.scrollHeight);
  el.style.height = `${String(h)}px`;
  el.style.overflowY = el.scrollHeight > max ? "auto" : "hidden";
}
